import { DEFAULT_TONE_COLOR_SETTINGS } from "#default-tone-color-settings";
import { PLECO_TONE_COLORS } from "#pleco-tone-colors";
import { SKRITTER_TONE_COLORS } from "#skritter-tone-colors";
import onetime from "@-/com.npmjs.onetime";

export type ToneColorPreset = "custom" | "pleco" | "skritter";

export const TONE_COLOR_PRESETS = [
  "pleco",
  "skritter",
  "custom",
] as const satisfies readonly ToneColorPreset[];

/** Light-mode hex values for a tone preset (Pleco / Skritter); `tone0Color` is the neutral tone. */
export type ToneColors = {
  tone0Color: string;
  tone1Color: string;
  tone2Color: string;
  tone3Color: string;
  tone4Color: string;
};

/** All `user_settings` tone color name/hex columns (`tone0*` is the neutral tone). */
export type UserSettingsToneColors = {
  tone0ColorDarkHex: string;
  tone0ColorDarkName: string;
  tone0ColorLightHex: string;
  tone0ColorLightName: string;
  tone1ColorDarkHex: string;
  tone1ColorDarkName: string;
  tone1ColorLightHex: string;
  tone1ColorLightName: string;
  tone2ColorDarkHex: string;
  tone2ColorDarkName: string;
  tone2ColorLightHex: string;
  tone2ColorLightName: string;
  tone3ColorDarkHex: string;
  tone3ColorDarkName: string;
  tone3ColorLightHex: string;
  tone3ColorLightName: string;
  tone4ColorDarkHex: string;
  tone4ColorDarkName: string;
  tone4ColorLightHex: string;
  tone4ColorLightName: string;
};

const PRESET_COLOR_LIST = [
  { colors: PLECO_TONE_COLORS, preset: "pleco" },
  { colors: SKRITTER_TONE_COLORS, preset: "skritter" },
] as const;

/** Indexed by tone number: `[0]` is the neutral tone. */
const TONE_LIGHT_NAME_LIST = [
  DEFAULT_TONE_COLOR_SETTINGS.tone0ColorLightName,
  DEFAULT_TONE_COLOR_SETTINGS.tone1ColorLightName,
  DEFAULT_TONE_COLOR_SETTINGS.tone2ColorLightName,
  DEFAULT_TONE_COLOR_SETTINGS.tone3ColorLightName,
  DEFAULT_TONE_COLOR_SETTINGS.tone4ColorLightName,
] as const;

function normalizeHexColor(color: string) {
  return color.trim().toLowerCase();
}

function toneColorsMatch(left: ToneColors, right: ToneColors) {
  return (
    normalizeHexColor(left.tone0Color) === normalizeHexColor(right.tone0Color) &&
    normalizeHexColor(left.tone1Color) === normalizeHexColor(right.tone1Color) &&
    normalizeHexColor(left.tone2Color) === normalizeHexColor(right.tone2Color) &&
    normalizeHexColor(left.tone3Color) === normalizeHexColor(right.tone3Color) &&
    normalizeHexColor(left.tone4Color) === normalizeHexColor(right.tone4Color)
  );
}

export function userSettingsToneColorsToLightHex(colors: UserSettingsToneColors): ToneColors {
  return {
    tone0Color: colors.tone0ColorLightHex,
    tone1Color: colors.tone1ColorLightHex,
    tone2Color: colors.tone2ColorLightHex,
    tone3Color: colors.tone3ColorLightHex,
    tone4Color: colors.tone4ColorLightHex,
  };
}

function userSettingsToneColorsFromLightPreset(light: ToneColors): UserSettingsToneColors {
  return {
    tone0ColorDarkHex: DEFAULT_TONE_COLOR_SETTINGS.tone0ColorDarkHex,
    tone0ColorDarkName: DEFAULT_TONE_COLOR_SETTINGS.tone0ColorDarkName,
    tone0ColorLightHex: light.tone0Color,
    tone0ColorLightName: TONE_LIGHT_NAME_LIST[0],
    tone1ColorDarkHex: DEFAULT_TONE_COLOR_SETTINGS.tone1ColorDarkHex,
    tone1ColorDarkName: DEFAULT_TONE_COLOR_SETTINGS.tone1ColorDarkName,
    tone1ColorLightHex: light.tone1Color,
    tone1ColorLightName: TONE_LIGHT_NAME_LIST[1],
    tone2ColorDarkHex: DEFAULT_TONE_COLOR_SETTINGS.tone2ColorDarkHex,
    tone2ColorDarkName: DEFAULT_TONE_COLOR_SETTINGS.tone2ColorDarkName,
    tone2ColorLightHex: light.tone2Color,
    tone2ColorLightName: TONE_LIGHT_NAME_LIST[2],
    tone3ColorDarkHex: DEFAULT_TONE_COLOR_SETTINGS.tone3ColorDarkHex,
    tone3ColorDarkName: DEFAULT_TONE_COLOR_SETTINGS.tone3ColorDarkName,
    tone3ColorLightHex: light.tone3Color,
    tone3ColorLightName: TONE_LIGHT_NAME_LIST[3],
    tone4ColorDarkHex: DEFAULT_TONE_COLOR_SETTINGS.tone4ColorDarkHex,
    tone4ColorDarkName: DEFAULT_TONE_COLOR_SETTINGS.tone4ColorDarkName,
    tone4ColorLightHex: light.tone4Color,
    tone4ColorLightName: TONE_LIGHT_NAME_LIST[4],
  };
}

export function getToneColorPreset(colors: UserSettingsToneColors): ToneColorPreset {
  const lightHex = userSettingsToneColorsToLightHex(colors);
  for (const { colors: presetColors, preset } of PRESET_COLOR_LIST) {
    if (toneColorsMatch(lightHex, presetColors)) {
      return preset;
    }
  }
  return "custom";
}

export function toneColorsForPreset(preset: "pleco" | "skritter"): UserSettingsToneColors {
  const light = preset === "pleco" ? PLECO_TONE_COLORS : SKRITTER_TONE_COLORS;
  return userSettingsToneColorsFromLightPreset(light);
}

export const getDefaultUserSettingsToneColors = onetime((): UserSettingsToneColors =>
  userSettingsToneColorsFromLightPreset({
    tone0Color: DEFAULT_TONE_COLOR_SETTINGS.tone0ColorLightHex,
    tone1Color: DEFAULT_TONE_COLOR_SETTINGS.tone1ColorLightHex,
    tone2Color: DEFAULT_TONE_COLOR_SETTINGS.tone2ColorLightHex,
    tone3Color: DEFAULT_TONE_COLOR_SETTINGS.tone3ColorLightHex,
    tone4Color: DEFAULT_TONE_COLOR_SETTINGS.tone4ColorLightHex,
  }),
);
